import {
  ClassifierValue,
  DirSportAgeGroup,
  GroupTypesSportAndDisciplinesDocument,
  SportTrainingStageFragment,
  TypeSportsDocument,
  TypeSportStagePrepareCreateMutationHookResult,
  TypeSportStagePrepareRemoveMutationHookResult,
  TypeSportStagePrepareUpdateMutationHookResult,
  useTypeSportStagePrepareCreateMutation,
  useTypeSportStagePrepareRemoveMutation,
  useTypeSportStagePrepareUpdateMutation,
  useTypeSportStagePreparingQuery,
} from '../../../api';
import { useForm } from 'react-hook-form';
import React, { FC, useState } from 'react';
import { Button, Grid, IconButton, List, ListItem, ListItemSecondaryAction, ListItemText } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import getMessage from '../../../messages';
import { useSnackbar } from 'notistack';
import { Input } from '../../Inputs';
import SaveIcon from '@material-ui/icons/Save';
import RemoveIcon from '@material-ui/icons/Delete';
import { RanksAndTitle, StagePreparing } from '../../../module';

const StagePreparingWidget: FC<{ readonly?: boolean; id: string }> = ({ readonly, id }) => {
  const { data } = useTypeSportStagePreparingQuery({ variables: { id } });
  const { handleSubmit, getValues, register, control, errors, reset, formState } = useForm({
    defaultValues: {
      clsRanksAndAwards: null,
      clsTrainingStage: {},
      id: null,
    } as SportTrainingStageFragment,
  });
  const [mode, setMode] = useState<'list' | 'form'>('list');
  const [edit, setEdit] = useState<null | SportTrainingStageFragment>(null);
  const { enqueueSnackbar } = useSnackbar();
  const [create]: TypeSportStagePrepareCreateMutationHookResult = useTypeSportStagePrepareCreateMutation({
    update: (proxy, { data }) => {
      proxy.writeQuery({
        query: TypeSportsDocument,
        data: {
          ...data?.updateDirSport,
        },
      });
    },
  });

  const [remove]: TypeSportStagePrepareRemoveMutationHookResult = useTypeSportStagePrepareRemoveMutation({
    update: (proxy, { data }) => {
      proxy.writeQuery({
        query: TypeSportsDocument,
        data: {
          ...data?.updateDirSport,
        },
      });
    },
  });

  const [update]: TypeSportStagePrepareUpdateMutationHookResult = useTypeSportStagePrepareUpdateMutation({
    update: (proxy, { data }) => {
      proxy.writeQuery({
        query: TypeSportsDocument,
        data: {
          ...data?.updateDirSportTrainingStage,
        },
      });
    },
  });

  const onRemove = async (value: string, label: string) => {
    try {
      await remove({
        variables: {
          id,
          idItem: value,
        },
      });
      enqueueSnackbar(`Половозрастная группа "${label}" успешно удалена`, { variant: 'success' });
    } catch (e) {
      enqueueSnackbar(getMessage(e.message), { variant: 'error' });
    }
  };

  const onSubmit = handleSubmit(async (values: SportTrainingStageFragment) => {
    try {
      if (edit) {
        await update({
          variables: {
            id: values?.id,
            maxWomanAge: Number(values?.maxWomanAge),
            minGroupSize: Number(values?.minGroupSize),
            maxGroupSize: Number(values?.maxGroupSize),
            minWomanAge: Number(values?.minWomanAge),
            maxManAge: Number(values?.maxManAge),
            minManAge: Number(values?.minManAge),
            idStep: values?.clsTrainingStage?.value,
            idRank: values?.clsRanksAndAwards?.map((rank) => ({ id: rank.value })),
          },
        });
        setEdit(null);
        enqueueSnackbar(`Этап подготовки "${values?.clsTrainingStage?.label}" успешно обновлен`, {
          variant: 'success',
        });
      } else {
        await create({
          variables: {
            id,
            maxWomanAge: Number(values?.maxWomanAge),
            minGroupSize: Number(values?.minGroupSize),
            maxGroupSize: Number(values?.maxGroupSize),
            minWomanAge: Number(values?.minWomanAge),
            maxManAge: Number(values?.maxManAge),
            minManAge: Number(values?.minManAge),
            idStep: values?.clsTrainingStage?.value,
            idRank: values?.clsRanksAndAwards?.map((rank) => ({ id: rank.value })),
          },
        });
        enqueueSnackbar(`Этап подготовки "${values?.clsTrainingStage?.label}" успешно добавлен`, {
          variant: 'success',
        });
      }
      reset();
    } catch (e) {
      enqueueSnackbar(getMessage(e.message), { variant: 'error' });
    }
    setMode('list');
  });

  return (
    <Grid container>
      {!readonly && !(mode === 'form' || edit) && (
        <Grid item md={12}>
          <IconButton
            onClick={() => {
              setMode('form');
              reset({ clsRanksAndAwards: [], clsTrainingStage: {}, id: null });
            }}
            edge="end"
            aria-label="add"
            type="button"
          >
            <AddIcon color="primary" />
          </IconButton>
        </Grid>
      )}
      {(mode === 'form' || edit) && (
        <form onSubmit={onSubmit} style={{ minWidth: '100%' }}>
          <Grid item md={12} container spacing={2} direction="row">
            <input type="hidden" name="id" ref={register()} />
            <Grid item md={6}>
              <StagePreparing
                label="Этап подготовки"
                error={!!errors['clsTrainingStage']}
                name="clsTrainingStage"
                control={control}
                rules={{ required: true }}
              />
            </Grid>
            <Grid item md={6}>
              <RanksAndTitle
                label="Разряды/звания"
                error={!!errors['clsRanksAndAwards']}
                name="clsRanksAndAwards"
                control={control}
                multiple
              />
            </Grid>
            <Grid item md={3}>
              <Input
                label="Мин. возраст у мужчин"
                type="number"
                control={control}
                error={!!errors['minManAge']}
                name="minManAge"
              />
            </Grid>
            <Grid item md={3}>
              <Input
                label="Макс. возраст у мужчин"
                type="number"
                control={control}
                error={!!errors['maxManAge']}
                name="maxManAge"
              />
            </Grid>
            <Grid item md={3}>
              <Input
                label="Мин. возраст у женщин"
                type="number"
                control={control}
                error={!!errors['minWomanAge']}
                name="minWomanAge"
              />
            </Grid>
            <Grid item md={3}>
              <Input
                label="Макс. возраст у женщин"
                type="number"
                control={control}
                error={!!errors['maxWomanAge']}
                name="maxWomanAge"
              />
            </Grid>
            <Grid item md={3}>
              <Input
                label="Мин. наполняемость группы"
                type="number"
                control={control}
                error={!!errors['minGroupSize']}
                name="minGroupSize"
              />
            </Grid>
            <Grid item md={3}>
              <Input
                label="Макс. наполняемость группы"
                type="number"
                control={control}
                error={!!errors['maxGroupSize']}
                name="maxGroupSize"
              />
            </Grid>
            <Grid item md={12} container spacing={1}>
              <Grid item>
                <Button
                  variant="outlined"
                  color="primary"
                  type="button"
                  onClick={() => {
                    setMode('list');
                    setEdit(null);
                  }}
                  size="small"
                >
                  Отмена
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant="outlined"
                  color="primary"
                  type="submit"
                  size="small"
                  startIcon={<SaveIcon color="primary" />}
                  disabled={!formState.isDirty}
                >
                  Сохранить
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </form>
      )}
      {mode === 'list' && !edit && (
        <Grid item md={12}>
          <List>
            {data?.dirSport?.trainingStages?.map((item) => (
              <ListItem key={item.id} alignItems="flex-start">
                <ListItemText
                  primary={item.clsTrainingStage.label}
                  secondary={`Мин. возраст у мужчины: ${item.minManAge || '-'}
Макс. возраст у мужчины : ${item.maxManAge || '-'}
Мин. возраст у женщины: ${item.minWomanAge || '-'}
Макс. возраст у женщины : ${item.maxWomanAge || '-'}
Мин. наполняемость группы: ${item.minGroupSize || '-'}
Макс. наполняемость группы: ${item.maxGroupSize || '-'}
Разряды/Звания: ${item.clsRanksAndAwards?.reduce(
                    (accumulator, currentValue) => accumulator + `, ${currentValue.label}`,
                    '',
                  )}`}
                />
                {!readonly && (
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      aria-label="редактировать"
                      onClick={() => {
                        reset(item);
                        setEdit(item);
                      }}
                    >
                      <EditIcon color="primary" />
                    </IconButton>
                    <IconButton
                      edge="end"
                      aria-label="редактировать"
                      onClick={() => onRemove(item.id, item.clsTrainingStage?.label)}
                    >
                      <RemoveIcon color="error" />
                    </IconButton>
                  </ListItemSecondaryAction>
                )}
              </ListItem>
            ))}
          </List>
        </Grid>
      )}
    </Grid>
  );
};

export default StagePreparingWidget;
